import {
  Card,
  Checkbox,
  CrudMode,
  DataArray,
  DataDisplay,
  InfoBanner,
  pn,
  Stack,
  Typography,
  ValidationErrors,
} from "@compass/core";
import { Select } from "components";
import { LkRole, LkRoleId, LkUserDataAssignmentTypeId, User, UserDto, UserEditResource } from "generated/openApi";
import produce from "immer";
import "moment-timezone";

export const DataAssignment = (props: {
  crudMode: CrudMode;
  appUserRoleId: number | undefined;
  userDto: UserDto;
  setUserDto: (value: UserDto) => void;
  editResource: UserEditResource;
  validationErrors: ValidationErrors | string[];
  selectedRole: LkRole | undefined;
}) => {
  const UNRESTRICTED: string = "Unrestricted (All)";

  return (
    <Card transparentHeader={true} header="Data Assignment" fullHeight>
      <Stack spacing="md">
        <InfoBanner title="">
          <Typography variant="caption" color="primary">
            Data can be configured for {props.selectedRole?.name} role to allow only access to specified Regions,
            Districts or Schools. Leave blank for unrestricted access to all data.
          </Typography>
        </InfoBanner>

        <Stack>
          {(props.crudMode === "view" || (props.appUserRoleId != LkRoleId.Cobro && props.appUserRoleId != LkRoleId.Administrator)) && (
            <DataDisplay>
              {props.userDto.user!.dataAssignmentType?.id ? props.userDto.user!.dataAssignmentType?.name : UNRESTRICTED}
            </DataDisplay>
          )}
          {(props.crudMode === "edit" &&
            <Select
              placeholder={UNRESTRICTED}
              disabled={props.appUserRoleId != LkRoleId.Cobro && props.appUserRoleId != LkRoleId.Administrator}
              value={props.editResource.userDataAssignmentTypes?.find((x) => x.id === props.userDto.user!.dataAssignmentTypeId)}
              options={props.editResource.userDataAssignmentTypes}
              getOptionLabel={(option) => option.name}
              onChange={(e, value) =>
                props.setUserDto(
                  produce(props.userDto, (draft) => {
                    draft.user!.dataAssignmentTypeId = value?.id;
                  })
                )
              }
              errors={props.validationErrors[pn<User>("dataAssignmentTypeId")]}
            />
          )}
        </Stack>

        <Stack>
          {props.userDto.user!.dataAssignmentTypeId === Number(LkUserDataAssignmentTypeId.Region) && (
            <>
              {props.crudMode === "view" && <DataArray value={props.userDto.user!.regions?.map((x) => x.name)} />}

              {props.crudMode === "edit" &&
                props.editResource.regions?.map((x) => (
                  <Stack>
                    <Checkbox
                      key={x.id}
                      disabled={props.appUserRoleId != LkRoleId.Cobro && props.appUserRoleId != LkRoleId.Administrator}
                      checked={props.userDto.user!.regions?.map((x) => x.id).includes(x.id)}
                      onClick={() =>
                        props.setUserDto(
                          produce(props.userDto, (draft) => {
                            const index: number | undefined = props.userDto.user!.regions?.map((x) => x.id).indexOf(x.id);
                            if (index !== undefined && index >= 0) draft.user!.regions?.splice(index, 1);
                            else draft.user!.regions?.push(x);
                          })
                        )
                      }
                      label={x.name}
                    />
                  </Stack>
                ))}
            </>
          )}
        </Stack>
        <Stack>
          {props.userDto.user!.dataAssignmentTypeId === Number(LkUserDataAssignmentTypeId.District) && (
            <>
              {props.crudMode === "view" && <DataArray value={props.userDto.user!.districts?.map((x) => x.name)} />}

              {props.crudMode === "edit" &&
                props.editResource.districts?.map((x) => (
                  <Stack>
                    <Checkbox
                      key={x.id}
                      disabled={props.appUserRoleId != LkRoleId.Cobro && props.appUserRoleId != LkRoleId.Administrator}
                      checked={props.userDto.user!.districts?.map((x) => x.id).includes(x.id)}
                      onClick={() =>
                        props.setUserDto(
                          produce(props.userDto, (draft) => {
                            const index: number | undefined = props.userDto.user!.districts?.map((x) => x.id).indexOf(x.id);
                            if (index !== undefined && index >= 0) draft.user!.districts?.splice(index, 1);
                            else draft.user!.districts?.push(x);
                          })
                        )
                      }
                      label={x.name}
                    />
                  </Stack>
                ))}
            </>
          )}
        </Stack>
        <Stack>
          {props.userDto.user!.dataAssignmentTypeId === Number(LkUserDataAssignmentTypeId.School) && (
            <>
              {props.crudMode === "view" && <DataArray value={props.userDto.user!.schools?.map((x) => x.name)} />}

              {props.crudMode === "edit" &&
                props.editResource.schools?.map((x) => (
                  <Stack>
                    <Checkbox
                      key={x.id}
                      disabled={props.appUserRoleId != LkRoleId.Cobro && props.appUserRoleId != LkRoleId.Administrator}
                      checked={props.userDto.user!.schools?.map((x) => x.id).includes(x.id)}
                      onClick={() =>
                        props.setUserDto(
                          produce(props.userDto, (draft) => {
                            const index: number | undefined = props.userDto.user!.schools?.map((x) => x.id).indexOf(x.id);
                            if (index !== undefined && index >= 0) draft.user!.schools?.splice(index, 1);
                            else draft.user!.schools?.push(x);
                          })
                        )
                      }
                      label={x.name}
                    />
                  </Stack>
                ))}
            </>
          )}
        </Stack>
        <Stack>
          {props.userDto.user!.dataAssignmentTypeId === Number(LkUserDataAssignmentTypeId.Program) && (
            <>
              {props.crudMode === "view" && <DataArray value={props.userDto.user!.programs?.map((x) => x.name)} />}

              {props.crudMode === "edit" &&
                props.editResource.programs?.map((x) => (
                  <Stack>
                    <Checkbox
                      key={x.id}
                      disabled={props.appUserRoleId != LkRoleId.Cobro && props.appUserRoleId != LkRoleId.Administrator}
                      checked={props.userDto.user!.programs?.map((x) => x.id).includes(x.id)}
                      onClick={() =>
                        props.setUserDto(
                          produce(props.userDto, (draft) => {
                            const index: number | undefined = props.userDto.user!.programs?.map((x) => x.id).indexOf(x.id);
                            if (index !== undefined && index >= 0) draft.user!.programs?.splice(index, 1);
                            else draft.user!.programs?.push(x);
                          })
                        )
                      }
                      label={x.name}
                    />
                  </Stack>
                ))}
            </>
          )}
        </Stack>
      </Stack>
    </Card>
  );
};
